import { CommandPalette } from "@/components/CommandPalette";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FocusOnRouteChange } from "@/components/FocusOnRouteChange";
import { GlobalHotkeys } from "@/components/GlobalHotkeys";
import { OfflineBanner } from "@/components/OfflineBanner";
import { RateLimitInterceptor } from "@/components/RateLimitInterceptor";
import { ShortcutHelpModal } from "@/components/ShortcutHelpModal";
import { StaleSessionInterceptor } from "@/components/StaleSessionInterceptor";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { DevLayout } from "@/components/layout/DevLayout";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { BackToTop } from "@/components/ui/BackToTop";
import { CookieBanner } from "@/components/ui/CookieBanner";
import { ScrollToTop } from "@/components/ui/ScrollToTop";
import { ToastProvider } from "@/components/ui/Toast";
import { AuthProvider } from "@/hooks/useAuth";
import { Showcase } from "@/pages/Showcase";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "./styles/global.css";

const AdminConsole = lazy(() =>
  import("@/pages/AdminConsole").then((m) => ({ default: m.AdminConsole })),
);
const AdminLogin = lazy(() =>
  import("@/pages/AdminLogin").then((m) => ({ default: m.AdminLogin })),
);
const Community = lazy(() =>
  import("@/pages/Community").then((m) => ({ default: m.Community })),
);
const DevPortal = lazy(() =>
  import("@/pages/DevPortal").then((m) => ({ default: m.DevPortal })),
);
const ForgotPassword = lazy(() =>
  import("@/pages/ForgotPassword").then((m) => ({ default: m.ForgotPassword })),
);
const JoinForm = lazy(() =>
  import("@/pages/JoinForm").then((m) => ({ default: m.JoinForm })),
);
const Login = lazy(() =>
  import("@/pages/Login").then((m) => ({ default: m.Login })),
);
const Register = lazy(() =>
  import("@/pages/Register").then((m) => ({ default: m.Register })),
);
const ResetPassword = lazy(() =>
  import("@/pages/ResetPassword").then((m) => ({ default: m.ResetPassword })),
);
const VerifyEmail = lazy(() =>
  import("@/pages/VerifyEmail").then((m) => ({ default: m.VerifyEmail })),
);
const RepoView = lazy(() =>
  import("@/pages/RepoView").then((m) => ({ default: m.RepoView })),
);
const FileViewer = lazy(() =>
  import("@/pages/FileViewer").then((m) => ({ default: m.FileViewer })),
);
const Playground = lazy(() =>
  import("@/pages/Playground").then((m) => ({ default: m.Playground })),
);
const ContributorProfilePage = lazy(() =>
  import("@/pages/ContributorProfile").then((m) => ({
    default: m.ContributorProfilePage,
  })),
);
const ChangelogPage = lazy(() =>
  import("@/pages/ChangelogPage").then((m) => ({
    default: m.ChangelogPage,
  })),
);
const OnboardingWizard = lazy(() =>
  import("@/pages/OnboardingWizard").then((m) => ({
    default: m.OnboardingWizard,
  })),
);
const SdkDocs = lazy(() =>
  import("@/pages/SdkDocs").then((m) => ({
    default: m.SdkDocs,
  })),
);
const NotificationsPage = lazy(() =>
  import("@/pages/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const ContributorsDirectory = lazy(() =>
  import("@/pages/ContributorsDirectory").then((m) => ({
    default: m.ContributorsDirectory,
  })),
);
const SearchPage = lazy(() =>
  import("@/pages/SearchPage").then((m) => ({
    default: m.SearchPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/pages/SettingsPage").then((m) => ({
    default: m.SettingsPage,
  })),
);
const NotFound = lazy(() =>
  import("@/pages/NotFound").then((m) => ({
    default: m.NotFound,
  })),
);
const ActivityFeed = lazy(() =>
  import("@/pages/ActivityFeed").then((m) => ({
    default: m.ActivityFeed,
  })),
);
const IssuesPage = lazy(() =>
  import("@/pages/IssuesPage").then((m) => ({
    default: m.IssuesPage,
  })),
);
const ApiDocsPage = lazy(() =>
  import("@/pages/ApiDocsPage").then((m) => ({
    default: m.ApiDocsPage,
  })),
);

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <RateLimitInterceptor />
          <BrowserRouter
            future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
          >
            <StaleSessionInterceptor />
            <a href="#main" className="skip-link">
              Skip to main content
            </a>
            <GlobalHotkeys />
            <CommandPalette />
            <FocusOnRouteChange />
            <Suspense
              fallback={
                <div className="flex items-center justify-center min-h-[60vh]">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              }
            >
              <Routes>
                {/* Admin pages — clean standalone layout */}
                <Route element={<AdminLayout />}>
                  <Route path="/open-admin" element={<AdminConsole />} />
                  <Route path="/admin-login" element={<AdminLogin />} />
                </Route>

                {/* Developer pages — focused dev layout */}
                <Route element={<DevLayout />}>
                  <Route path="/dev-portal" element={<DevPortal />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route
                    path="/notifications"
                    element={<NotificationsPage />}
                  />
                  <Route path="/onboarding" element={<OnboardingWizard />} />
                </Route>

                {/* Public pages — full navbar + footer */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Showcase />} />
                  <Route path="/join" element={<JoinForm />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/community" element={<Community />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/repos/:id" element={<RepoView />} />
                  <Route
                    path="/repos/:id/blob/:fileId/:filename"
                    element={<FileViewer />}
                  />
                  <Route path="/playground" element={<Playground />} />
                  <Route
                    path="/contributors/:username"
                    element={<ContributorProfilePage />}
                  />
                  <Route path="/changelog" element={<ChangelogPage />} />
                  <Route path="/docs" element={<SdkDocs />} />
                  <Route
                    path="/contributors"
                    element={<ContributorsDirectory />}
                  />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/activity" element={<ActivityFeed />} />
                  <Route path="/issues" element={<IssuesPage />} />
                  <Route path="/api-docs" element={<ApiDocsPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Suspense>
            <ScrollToTop />
            <BackToTop />
            <CookieBanner />
            <OfflineBanner />
            <ShortcutHelpModal />
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
